import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api from "../api/client";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const TYPES = [
  { v: "particulier", l: "Particulier" },
  { v: "entreprise", l: "Entreprise" },
];

const CIVILITES = ["M.", "Mme", "Mlle"];

export default function ClientEditDialog({ open, onClose, client, onUpdated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && client) {
      setForm({
        type: client.type || "particulier",
        civilite: client.civilite || "",
        nom: client.nom || "",
        prenom: client.prenom || "",
        raison_sociale: client.raison_sociale || "",
        telephone: client.telephone || "",
        email: client.email || "",
        adresse: client.adresse || "",
        ville: client.ville || "",
        pays: client.pays || "Sénégal",
        statut: client.statut || "actif",
        notes: client.notes || "",
        // Champs identité + pro (nécessaires pour le contrat MALIKA)
        profession: client.profession || "",
        employeur: client.employeur || "",
        numero_cni: client.numero_cni || client.numero_piece || "",
        piece_identite: client.piece_identite || "CNI",
        nationalite: client.nationalite || "Sénégalaise",
        date_naissance: client.date_naissance || "",
        lieu_naissance: client.lieu_naissance || "",
      });
    }
  }, [open, client]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, form);
      toast.success("Client mis à jour");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;
  const isEntreprise = form.type === "entreprise";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="client-edit-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E5F1] shrink-0">
          <DialogTitle className="font-display text-xl text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Modifier le client
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldSelect label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES} testid="ce-type" />
            <FieldSelect label="Statut" value={form.statut} onChange={(v) => setForm({ ...form, statut: v })} options={[{ v: "actif", l: "Actif" }, { v: "prospect", l: "Prospect" }, { v: "inactif", l: "Inactif" }]} />
          </div>

          {!isEntreprise && (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Civilité">
                <Select value={form.civilite || "none"} onValueChange={(v) => setForm({ ...form, civilite: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {CIVILITES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nom"><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="ce-nom" /></Field>
              <Field label="Prénom"><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></Field>
            </div>
          )}
          {isEntreprise && (
            <Field label="Raison sociale">
              <Input value={form.raison_sociale} onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })} />
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Téléphone *"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="ce-tel" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>

          <Field label="Adresse"><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville"><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></Field>
            <Field label="Pays"><Input value={form.pays} onChange={(e) => setForm({ ...form, pays: e.target.value })} /></Field>
          </div>

          {/* --- Identité (pour le contrat) --- */}
          {!isEntreprise && (
            <>
              <div className="pt-4 mt-2 border-t border-[#E2E5F1]">
                <div className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold mb-3">Identité & profession (nécessaires pour le contrat)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Profession">
                    <Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="Ex : Chef d'entreprise" data-testid="ce-profession" />
                  </Field>
                  <Field label="Employeur">
                    <Input value={form.employeur} onChange={(e) => setForm({ ...form, employeur: e.target.value })} placeholder="Ex : Sonatel SA" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <Field label="Pièce d'identité">
                    <Select value={form.piece_identite || "CNI"} onValueChange={(v) => setForm({ ...form, piece_identite: v })}>
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CNI">CNI</SelectItem>
                        <SelectItem value="Passeport">Passeport</SelectItem>
                        <SelectItem value="Permis">Permis de conduire</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="N° pièce d'identité">
                    <Input value={form.numero_cni} onChange={(e) => setForm({ ...form, numero_cni: e.target.value })} placeholder="Ex : 1 913 2000 02186" data-testid="ce-cni" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <Field label="Date de naissance">
                    <Input type="date" value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} data-testid="ce-dob" />
                  </Field>
                  <Field label="Lieu de naissance">
                    <Input value={form.lieu_naissance} onChange={(e) => setForm({ ...form, lieu_naissance: e.target.value })} placeholder="Ex : Dakar" />
                  </Field>
                  <Field label="Nationalité">
                    <Input value={form.nationalite} onChange={(e) => setForm({ ...form, nationalite: e.target.value })} placeholder="Ex : Sénégalaise" data-testid="ce-nat" />
                  </Field>
                </div>
              </div>
            </>
          )}

          <Field label="Notes internes">
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] resize-none" />
          </Field>

          <div className="flex gap-2 pt-3 border-t border-[#E2E5F1] -mx-6 px-6 sticky bottom-0 bg-white">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-[#E2E5F1] text-xs font-bold uppercase tracking-wider text-[#4B5775]">Annuler</button>
            <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ce-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}
